import { useCallback, useEffect, useState } from "react";
import {
  canOfferPwaInstall,
  detectPwaInstalled,
  isPwaInstalled,
  markPwaInstalledLocally,
  needsManualPwaInstall,
} from "@/lib/pwaInstall";
import {
  getDeferredPwaPrompt,
  setDeferredPwaPrompt,
  subscribeDeferredPwaPrompt,
} from "@/lib/pwaInstallCapture";

export function usePwaInstall() {
  const [deferredPrompt, setDeferredPrompt] = useState(() => getDeferredPwaPrompt());
  const [installed, setInstalled] = useState(() => isPwaInstalled());
  const [checking, setChecking] = useState(true);
  const [installing, setInstalling] = useState(false);
  const [installProgress, setInstallProgress] = useState(0);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      const detected = await detectPwaInstalled();
      if (!cancelled) {
        setInstalled(detected);
        setChecking(false);
      }
    })();

    const unsub = subscribeDeferredPwaPrompt((prompt) => {
      setDeferredPrompt(prompt);
    });

    const onInstalled = () => {
      markPwaInstalledLocally();
      setInstalled(true);
      setDeferredPrompt(null);
      setDeferredPwaPrompt(null);
      setInstalling(false);
      setInstallProgress(100);
    };

    const onDisplayModeChange = () => {
      if (isPwaInstalled()) setInstalled(true);
    };

    window.addEventListener("appinstalled", onInstalled);
    window.matchMedia("(display-mode: standalone)").addEventListener("change", onDisplayModeChange);

    const onVisibility = async () => {
      if (document.visibilityState !== "visible") return;
      const detected = await detectPwaInstalled();
      if (detected) setInstalled(true);
    };
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      cancelled = true;
      unsub();
      window.removeEventListener("appinstalled", onInstalled);
      window.matchMedia("(display-mode: standalone)").removeEventListener("change", onDisplayModeChange);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, []);

  const canInstall = !checking && canOfferPwaInstall() && !installed;

  const triggerInstall = useCallback(async () => {
    const prompt = deferredPrompt || getDeferredPwaPrompt();
    if (!prompt) return { outcome: "manual" };

    setInstalling(true);
    setInstallProgress(12);

    const progressTimer = window.setInterval(() => {
      setInstallProgress((p) => (p >= 88 ? p : p + 4));
    }, 280);

    let outcome = "dismissed";
    try {
      prompt.prompt();
      setInstallProgress(42);
      ({ outcome } = await prompt.userChoice);
      setInstallProgress(outcome === "accepted" ? 100 : 0);
      setDeferredPwaPrompt(null);
      setDeferredPrompt(null);
      if (outcome === "accepted") {
        markPwaInstalledLocally();
        setInstalled(true);
      }
      return { outcome };
    } finally {
      window.clearInterval(progressTimer);
      window.setTimeout(() => {
        setInstalling(false);
        if (outcome !== "accepted") setInstallProgress(0);
      }, outcome === "accepted" ? 600 : 200);
    }
  }, [deferredPrompt]);

  return {
    canInstall,
    installed,
    checking,
    installing,
    installProgress,
    deferredPrompt,
    hasNativePrompt: Boolean(deferredPrompt || getDeferredPwaPrompt()),
    needsManualInstall: needsManualPwaInstall(),
    triggerInstall,
  };
}
